"use client"

/**
 * MigrationBanner — Path Social → Outfame full-continuity banner.
 *
 * Shown ONLY to visitors arriving with the migration params
 * (utm_campaign=path_social_rebrand OR ref=ps OR ps_l=…). Visitors without
 * them render nothing. Dismissable; dismissal lasts the browser session.
 *
 * Honors their Path Social plan name and price via the `plan` (+ optional
 * `price`) params, and their handle via `username`. Language: EN/ES — the
 * `locale` prop wins (route locale on localized pages), then a `lang`/`locale`
 * URL param (our redirect links carry it because /es/setup 301s to /setup),
 * then navigator.language.
 */

import { useEffect, useState } from "react"
import Link from "next/link"
import { useSearchParams, usePathname } from "next/navigation"
import { XMarkIcon } from "@heroicons/react/24/outline"
import { isMigrationVisit, mapPsPlan, psPlanParam } from "@/lib/ps-migration"

const DISMISS_KEY = "ps-migration-banner-dismissed"

interface Copy {
    lead: string
    welcome: (username: string | null, plan: string, price: string | null) => string
    cta: string
    close: string
}

const COPY: Record<"en" | "es", Copy> = {
    en: {
        lead: "Path Social has upgraded to Outfame.",
        welcome: (username, plan, price) =>
            `Welcome back${username ? `, @${username}` : ""}! Your plan (${plan})${price ? ` and your price (${price})` : ""} carried over — plus Outfame's AI targeting and 30-day growth guarantee, all new for you. Nothing to re-enter.`,
        cta: "Continue setup →",
        close: "Dismiss"
    },
    es: {
        lead: "Path Social ahora es Outfame.",
        welcome: (username, plan, price) =>
            `¡Bienvenido de nuevo${username ? `, @${username}` : ""}! Tu plan (${plan})${price ? ` y tu precio (${price})` : ""} se han mantenido — y además estrenas la segmentación con IA y la garantía de crecimiento de 30 días de Outfame. No tienes que volver a escribir nada.`,
        cta: "Continuar →",
        close: "Cerrar"
    }
}

function priceLabel(price: number | null, explicit: string | null, lang: "en" | "es"): string | null {
    if (explicit) return explicit
    if (price == null) return null
    return lang === "es" ? `$${price}/mes, facturado anualmente` : `$${price}/mo, billed annually`
}

export default function MigrationBanner({ locale }: { locale?: string }) {
    const searchParams = useSearchParams()
    const pathname = usePathname()
    const [mounted, setMounted] = useState(false)
    const [dismissed, setDismissed] = useState(false)
    const [lang, setLang] = useState<"en" | "es">("en")

    useEffect(() => {
        setMounted(true)
        try {
            if (sessionStorage.getItem(DISMISS_KEY)) setDismissed(true)
        } catch {
            // private mode etc. — banner just stays dismissable per mount
        }
        const param = locale || searchParams.get("lang") || searchParams.get("locale") || ""
        const nav = typeof navigator !== "undefined" ? navigator.language : ""
        const detected = (param || nav || "en").toLowerCase()
        setLang(detected.startsWith("es") ? "es" : "en")
    }, [locale, searchParams])

    if (!mounted || dismissed) return null
    if (!isMigrationVisit(searchParams)) return null

    const copy = COPY[lang]
    const mapping = mapPsPlan(psPlanParam(searchParams))
    const username = (searchParams.get("username") || "").replace(/^@+/, "").trim() || null
    const price = priceLabel(mapping.psYearlyPrice, searchParams.get("price"), lang)

    const setupParams = new URLSearchParams()
    const carry = ["platform", "username", "email", "coupon", "offer", "ref", "utm_source", "utm_medium", "utm_campaign", "ps_l", "plan", "fbclid", "ttclid", "gclid", "lang"]
    carry.forEach((k) => {
        const v = searchParams.get(k)
        if (v) setupParams.set(k, v)
    })
    if (lang === "es" && !setupParams.get("lang")) setupParams.set("lang", "es")
    const setupHref = `/setup${setupParams.toString() ? `?${setupParams.toString()}` : ""}`
    const onSetup = /^\/(es|fr|de)?\/?setup\/?$/.test(pathname || "")

    const dismiss = () => {
        setDismissed(true)
        try {
            sessionStorage.setItem(DISMISS_KEY, "1")
        } catch {
            // ignore
        }
    }

    return (
        <div role="region" aria-label={copy.lead} className="sticky top-0 z-[10000000] w-full bg-[#141414] px-4 py-3 text-white">
            <div className="mx-auto flex max-w-[1120px] items-start gap-3">
                <span aria-hidden="true" className="mt-[2px] text-[16px] leading-[22px]">🔄</span>
                <p className="flex-1 text-[13px] leading-[20px] text-white/85 md:text-[14px] md:leading-[21px]">
                    <span className="font-semibold text-white">{copy.lead}</span>{" "}
                    {copy.welcome(username, mapping.psName, price)}
                    {!onSetup && (
                        <>
                            {" "}
                            <Link href={setupHref} className="whitespace-nowrap font-semibold text-[rgb(255,163,209)] underline decoration-[rgb(255,163,209)]/40 underline-offset-4 hover:decoration-[rgb(255,163,209)]">
                                {copy.cta}
                            </Link>
                        </>
                    )}
                </p>
                <button
                    type="button"
                    onClick={dismiss}
                    aria-label={copy.close}
                    className="shrink-0 rounded-md p-1 text-white/60 transition-colors hover:bg-white/10 hover:text-white"
                >
                    <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                </button>
            </div>
        </div>
    )
}
